<template>
  <div class="chat-end-phase">
    
    <!-- Chat Ended Message -->
    <div class="text-center py-6">

      <!-- Rating Section - Show only when not submitted -->
      <div v-if="!hasSubmitted" class="bg-white border border-gray-200 rounded-lg p-6">
        <h4 class="text-gray-800 text-base font-medium mb-4">
          Bewerten Sie unser Gespräch
        </h4>
        <p class="text-gray-600 text-sm mb-6">
          Wie zufrieden waren Sie mit unserem Service?
        </p>

        <!-- Star Rating -->
        <div class="flex justify-center space-x-2 mb-6">
          <button
            v-for="star in 5"
            :key="star"
            @click="setRating(star)"
            class="transition-all duration-200 transform hover:scale-110 focus:outline-none"
            :class="{
              'text-yellow-400': star <= selectedRating,
              'text-gray-300 hover:text-yellow-300': star > selectedRating
            }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" :fill="star <= selectedRating ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        </div>

        <!-- Rating Labels -->
        <div class="text-center mb-6">
          <span v-if="selectedRating === 0" class="text-gray-500 text-sm">Klicken Sie auf einen Stern zur Bewertung</span>
          <span v-else-if="selectedRating === 1" class="text-red-600 text-sm font-medium">Sehr unzufrieden</span>
          <span v-else-if="selectedRating === 2" class="text-orange-600 text-sm font-medium">Unzufrieden</span>
          <span v-else-if="selectedRating === 3" class="text-yellow-600 text-sm font-medium">Neutral</span>
          <span v-else-if="selectedRating === 4" class="text-green-600 text-sm font-medium">Zufrieden</span>
          <span v-else-if="selectedRating === 5" class="text-green-700 text-sm font-medium">Sehr zufrieden</span>
        </div>

        <!-- Optional Comment -->
        <div class="mb-6">
          <textarea
            v-model="comment"
            placeholder="Zusätzlicher Kommentar (optional)..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
            rows="3"
            maxlength="500"
          ></textarea>
          <p class="text-xs text-gray-400 mt-1 text-right">{{ comment.length }}/500</p>
        </div>

        <!-- Submit Button with textColor support -->
        <div class="flex flex-col space-y-3">
          <button
            @click="submitRating"
            :disabled="selectedRating === 0 || isSubmitting"
            class="cursor-pointer w-full px-6 py-3 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            :style="{ 
              backgroundColor: dynamicButtonColor,
              color: styles.textColor || '#ffffff'
            }"
            :class="{
              'hover:opacity-90': selectedRating > 0 && !isSubmitting,
              'opacity-50 cursor-not-allowed': selectedRating === 0 || isSubmitting
            }"
          >
            <span v-if="!isSubmitting">Bewertung Senden</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5" :style="{ color: styles.textColor || '#ffffff' }" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Wird gesendet...
            </span>
          </button>

          <!-- Skip Rating Button -->
          <button
            @click="skipRating"
            :disabled="isSubmitting"
            class="cursor-pointer w-full px-6 py-2 text-gray-600 text-sm font-medium hover:text-gray-800 transition-colors duration-200 disabled:opacity-50"
          >
            Bewertung überspringen
          </button>
        </div>
      </div>

      <!-- Thank You Message - Show after submission -->
      <div v-else class="bg-white border border-gray-200 rounded-lg p-6">
        <div class="text-center">
          <!-- Success Icon -->
          <div class="flex items-center justify-center mb-4">
            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          
          <!-- Thank You Text -->
          <h4 class="text-gray-800 text-lg font-semibold mb-2">
            Vielen Dank für Ihr Feedback!
          </h4>
          <p class="text-gray-600 text-sm mb-6">
            Ihre Bewertung hilft uns, unseren Service zu verbessern.
          </p>
          
          <!-- Show submitted rating -->
          <div v-if="selectedRating > 0" class="flex justify-center items-center space-x-1 mb-6">
            <span class="text-sm text-gray-600 mr-2">Ihre Bewertung:</span>
            <div class="flex space-x-1">
              <svg 
                v-for="star in 5"
                :key="star"
                class="w-5 h-5"
                :class="{
                  'text-yellow-400': star <= selectedRating,
                  'text-gray-300': star > selectedRating
                }"
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
            <span class="text-sm text-gray-600 ml-2">({{ selectedRating }}/5)</span>
          </div>
          
          <!-- Start New Chat Button with textColor support -->
          <button
            @click="startNewChat"
            class="w-full px-6 py-3 font-medium rounded-lg transition-all duration-200 hover:opacity-90"
            :style="{ 
              backgroundColor: dynamicButtonColor,
              color: styles.textColor || '#ffffff'
            }"
          >
            Neuen Chat starten
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { databases } from '../../../lib/appwrite'
import { appwriteConfig } from '../../../lib/appwrite'

export default {
  name: 'ChatEnd',
  props: {
    styles: {
      type: Object,
      default: () => ({
        buttonColor: null,
        bubbleColor: '#000000',
        textColor: '#ffffff' // ✅ Added textColor to default props
      })
    },
    customer: {
      type: Object,
      required: true
    },
    conversation: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedRating: 0,
      comment: '',
      isSubmitting: false,
      hasSubmitted: false
    }
  },
  computed: {
    // ✅ Dynamic button color that follows the same logic as other components
    dynamicButtonColor() {
      const color = this.styles.buttonColor || this.styles.bubbleColor || '#000000';
      console.log('🎨 ChatEnd: Using button color:', color, {
        buttonColor: this.styles.buttonColor,
        bubbleColor: this.styles.bubbleColor,
        final: color
      });
      return color;
    }
  },
  emits: ['rating-submitted', 'rating-skipped', 'start-new-chat'],
  mounted() {
    console.log('🎨 ChatEnd: Mounted with styles:', {
      buttonColor: this.styles.buttonColor,
      bubbleColor: this.styles.bubbleColor,
      textColor: this.styles.textColor, // ✅ Added textColor logging
      dynamicButtonColor: this.dynamicButtonColor
    });
  },
  methods: {
    setRating(rating) {
      if (!this.isSubmitting && !this.hasSubmitted) {
        this.selectedRating = rating;
      }
    },

    async submitRating() {
      if (this.selectedRating === 0 || this.isSubmitting || this.hasSubmitted) return;

      this.isSubmitting = true;

      try {
        console.log('📊 Submitting rating:', this.selectedRating);

        // ✅ Update conversation with rating - YOUR APPWRITE SCHEMA
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.conversationCollectionId,
          this.conversation.$id,
          {
            rating: this.selectedRating, // ✅ Integer field
            ratingComment: this.comment.trim() || null, // ✅ String field
            updated_at: new Date().toISOString()
          }
        );

        console.log('✅ Rating submitted successfully');
        
        this.hasSubmitted = true;

        // Emit to parent
        this.$emit('rating-submitted', {
          rating: this.selectedRating,
          comment: this.comment.trim(),
          conversation: this.conversation
        });

        // Don't auto-close anymore - user will manually start new chat
        // setTimeout(() => {
        //   this.$emit('chat-complete');
        // }, 3000);

      } catch (error) {
        console.error('❌ Error submitting rating:', error);
        alert('Fehler beim Senden der Bewertung. Bitte versuchen Sie es erneut.');
      } finally {
        this.isSubmitting = false;
      }
    },

    async skipRating() {
      if (this.isSubmitting || this.hasSubmitted) return;

      try {
        console.log('⏭️ Skipping rating');

        // ✅ Update conversation to mark as skipped - YOUR APPWRITE SCHEMA
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.conversationCollectionId,
          this.conversation.$id,
          {
            rating: 0, // ✅ 0 means skipped
            ratingComment: null, // ✅ No comment
            updated_at: new Date().toISOString()
          }
        );

        this.hasSubmitted = true;

        // Emit to parent
        this.$emit('rating-skipped', {
          conversation: this.conversation
        });

        // Don't auto-close for skip either - user will manually start new chat
        // setTimeout(() => {
        //   this.$emit('chat-complete');
        // }, 1500);

      } catch (error) {
        console.error('❌ Error skipping rating:', error);
        // Continue anyway
        this.$emit('rating-skipped', {
          conversation: this.conversation
        });
      }
    },

    // ✅ Handle start new chat with correct event name
    startNewChat() {
      console.log('🆕 Starting new chat');
      
      // Emit the event that NewChatBody is listening for
      this.$emit('restart-chat');
    }
  }
}
</script>

<style scoped>
.chat-end-phase {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Star hover effects */
button:hover svg {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}
</style>